import React, { useEffect, useState, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faPlus, faCheckCircle, faTimes,
  faImage, faCalendarAlt, faBuilding, faMoneyBill,
  faFileAlt, faUniversity, faLock, faLockOpen, faEdit, faUser,
  faCalculator, faArrowRight, faFileInvoiceDollar, faMinus,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import { fmtBaht } from '../../utils/formatMoney';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';
const TEXTAREA_CLASS = `${INPUT_CLASS} resize-y`;
const FILE_INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-red-50 dark:file:bg-red-900/30 file:text-red-600 dark:file:text-red-400 file:font-medium file:cursor-pointer';
const FILE_INPUT_BLUE_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-600 dark:file:text-blue-400 file:font-medium file:cursor-pointer';

interface BankSuspension {
  id: string;
  bank_id: string;
  suspended_amount: number;
  agency_name: string;
  details: string | null;
  responsible_person: string | null;
  suspension_date: string;
  attachment_url: string | null;
  status: 'suspended' | 'unsuspended';
  release_cost: number;
  remaining_amount: number;
  unsuspended_at: string | null;
  unsuspended_by: string | null;
  created_by: string | null;
  created_at: string;
  banks: {
    id: string;
    name: string;
    account_number: string;
    bank_code: string;
  } | null;
}

const BANK_COLORS: Record<string, string> = {
  SCB: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
  KBANK: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  BBL: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  KTB: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300',
  BAY: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  GSB: 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300',
  TMB: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
  TTB: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
  CIMB: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
};

const BankSuspensionPage: React.FC = () => {
  const [suspensions, setSuspensions] = useState<BankSuspension[]>([]);
  const [banks, setBanks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<BankSuspension | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState<'suspended' | 'unsuspended'>('suspended');
  const [unsuspendingId, setUnsuspendingId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);
  const [unsuspendItem, setUnsuspendItem] = useState<BankSuspension | null>(null);
  const [releaseCost, setReleaseCost] = useState('');

  const [formData, setFormData] = useState({
    bank_id: '',
    suspended_amount: '',
    agency_name: '',
    details: '',
    responsible_person: '',
    suspension_date: new Date().toISOString().split('T')[0],
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);

  const [editFormData, setEditFormData] = useState({
    bank_id: '',
    suspended_amount: '',
    agency_name: '',
    details: '',
    responsible_person: '',
    suspension_date: '',
  });
  const [editAttachment, setEditAttachment] = useState<File | null>(null);
  const [editAttachmentPreview, setEditAttachmentPreview] = useState<string | null>(null);
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suspRes, bankRes] = await Promise.all([
        merchantApi.get('/admin/bank-suspensions'),
        merchantApi.get('/admin/banks?with_merchants=true'),
      ]);
      setSuspensions(suspRes.data.data || []);
      setBanks(bankRes.data.data || []);
    } catch (error: any) {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.bank_id || !formData.agency_name || !formData.suspension_date) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    try {
      setCreating(true);
      const fd = new FormData();
      fd.append('bank_id', formData.bank_id);
      fd.append('suspended_amount', formData.suspended_amount || '0');
      fd.append('agency_name', formData.agency_name);
      fd.append('details', formData.details);
      fd.append('responsible_person', formData.responsible_person);
      fd.append('suspension_date', formData.suspension_date);
      if (attachment) {
        fd.append('attachment', attachment);
      }

      const response = await merchantApi.post('/admin/bank-suspensions/create', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        toast.success('เพิ่มรายการบัญชีถูกระงับสำเร็จ');
        setShowCreateModal(false);
        resetForm();
        loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setCreating(false);
    }
  };

  const openUnsuspendModal = (item: BankSuspension) => {
    setUnsuspendItem(item);
    setReleaseCost('');
    setShowUnsuspendModal(true);
  };

  const handleUnsuspend = async () => {
    if (!unsuspendItem) return;

    try {
      setUnsuspendingId(unsuspendItem.id);
      const response = await merchantApi.put(`/admin/bank-suspensions/${unsuspendItem.id}/unsuspend`, {
        release_cost: releaseCost || '0',
      });
      if (response.data.success) {
        toast.success('ปลดระงับบัญชีเรียบร้อยแล้ว');
        setShowUnsuspendModal(false);
        setUnsuspendItem(null);
        loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setUnsuspendingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      bank_id: '',
      suspended_amount: '',
      agency_name: '',
      details: '',
      responsible_person: '',
      suspension_date: new Date().toISOString().split('T')[0],
    });
    setAttachment(null);
    setAttachmentPreview(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ไฟล์มีขนาดใหญ่เกิน 5MB');
        return;
      }
      setAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => setAttachmentPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const openEditModal = (item: BankSuspension) => {
    setEditingItem(item);
    setEditFormData({
      bank_id: item.bank_id,
      suspended_amount: String(item.suspended_amount || ''),
      agency_name: item.agency_name,
      details: item.details || '',
      responsible_person: item.responsible_person || '',
      suspension_date: item.suspension_date?.split('T')[0] || '',
    });
    setEditAttachment(null);
    setEditAttachmentPreview(null);
    setRemoveExistingAttachment(false);
    setShowEditModal(true);
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ไฟล์มีขนาดใหญ่เกิน 5MB');
        return;
      }
      setEditAttachment(file);
      setRemoveExistingAttachment(false);
      const reader = new FileReader();
      reader.onloadend = () => setEditAttachmentPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    if (!editFormData.bank_id || !editFormData.agency_name || !editFormData.suspension_date) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    try {
      setUpdating(true);
      const fd = new FormData();
      fd.append('bank_id', editFormData.bank_id);
      fd.append('suspended_amount', editFormData.suspended_amount || '0');
      fd.append('agency_name', editFormData.agency_name);
      fd.append('details', editFormData.details);
      fd.append('responsible_person', editFormData.responsible_person);
      fd.append('suspension_date', editFormData.suspension_date);
      if (editAttachment) {
        fd.append('attachment', editAttachment);
      }
      if (removeExistingAttachment && !editAttachment) {
        fd.append('remove_attachment', 'true');
      }

      const response = await merchantApi.put(`/admin/bank-suspensions/${editingItem.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        toast.success('อัพเดทรายการสำเร็จ');
        setShowEditModal(false);
        setEditingItem(null);
        loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setUpdating(false);
    }
  };

  const filtered = useMemo(
    () => suspensions.filter((s) => s.status === activeTab),
    [suspensions, activeTab],
  );

  const suspendedCount = suspensions.filter((s) => s.status === 'suspended').length;
  const unsuspendedCount = suspensions.filter((s) => s.status === 'unsuspended').length;
  const totalSuspendedAmount = suspensions
    .filter((s) => s.status === 'suspended')
    .reduce((sum, s) => sum + (s.suspended_amount || 0), 0);

  const unsuspendedItems = suspensions.filter((s) => s.status === 'unsuspended');
  const totalUnsuspendedAmount = unsuspendedItems.reduce((sum, s) => sum + (s.suspended_amount || 0), 0);
  const totalReleaseCost = unsuspendedItems.reduce((sum, s) => sum + (s.release_cost || 0), 0);
  const totalRemainingAmount = unsuspendedItems.reduce((sum, s) => sum + (s.remaining_amount || 0), 0);

  const unsuspendPreviewRemaining = unsuspendItem
    ? (unsuspendItem.suspended_amount || 0) - (parseFloat(releaseCost) || 0)
    : 0;

  const statCards: StatCardDef[] = [
    {
      icon: faLock,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'บัญชีถูกระงับ',
      value: <span className="text-red-600 dark:text-red-400">{suspendedCount.toLocaleString()}</span>,
    },
    {
      icon: faLockOpen,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ปลดระงับแล้ว',
      value: <span className="text-green-600 dark:text-green-400">{unsuspendedCount.toLocaleString()}</span>,
    },
    {
      icon: faMoneyBill,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'ยอดถูกระงับรวม',
      value: <span className="text-orange-600 dark:text-orange-400">{fmtBaht(totalSuspendedAmount)}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'ยอดปลดอายัดรวม',
      value: <span className="text-blue-600 dark:text-blue-400">{fmtBaht(totalUnsuspendedAmount)}</span>,
    },
  ];

  const reportCards: StatCardDef[] = [
    {
      icon: faFileInvoiceDollar,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'จำนวนรายการปลดระงับ',
      value: <span className="text-gray-800 dark:text-slate-100">{unsuspendedItems.length}</span>,
    },
    {
      icon: faBan,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ยอดถูกระงับรวม',
      value: <span className="text-red-600 dark:text-red-400">{fmtBaht(totalUnsuspendedAmount)}</span>,
    },
    {
      icon: faCalculator,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'ค่าปลดอายัดรวม',
      value: <span className="text-orange-600 dark:text-orange-400">{fmtBaht(totalReleaseCost)}</span>,
    },
    {
      icon: faMoneyBill,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ยอดคงเหลือรวม',
      value: <span className="text-green-600 dark:text-green-400">{fmtBaht(totalRemainingAmount)}</span>,
    },
  ];

  const renderBankCell = (item: BankSuspension) => {
    const bankCode = item.banks?.bank_code || '';
    const colorClass = BANK_COLORS[bankCode] || 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200';
    return (
      <div className="flex items-center gap-2 min-w-0">
        <span className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${colorClass}`}>{bankCode}</span>
        <div className="min-w-0">
          <p className="font-medium text-gray-800 dark:text-slate-100 truncate">{item.banks?.name || '-'}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{item.banks?.account_number || '-'}</p>
        </div>
      </div>
    );
  };

  const columns: Column<BankSuspension>[] = useMemo(() => {
    const base: Column<BankSuspension>[] = [
      { key: 'bank', header: 'บัญชีธนาคาร', render: renderBankCell },
      {
        key: 'amount',
        header: 'ยอดถูกระงับ',
        render: (item) => (
          <span className="font-semibold text-red-600 dark:text-red-400">{fmtBaht(item.suspended_amount)}</span>
        ),
      },
      {
        key: 'agency',
        header: 'หน่วยงาน',
        render: (item) => (
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faBuilding} className="text-gray-400 dark:text-slate-500 text-xs" />
            <span className="text-gray-700 dark:text-slate-200">{item.agency_name}</span>
          </div>
        ),
      },
      {
        key: 'details',
        header: 'รายละเอียด',
        render: (item) => (
          <span className="text-gray-600 dark:text-slate-300 max-w-xs truncate block">{item.details || '-'}</span>
        ),
      },
      {
        key: 'responsible',
        header: 'ผู้รับผิดชอบ',
        render: (item) => item.responsible_person ? (
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faUser} className="text-gray-400 dark:text-slate-500 text-xs" />
            <span className="text-gray-700 dark:text-slate-200">{item.responsible_person}</span>
          </div>
        ) : (
          <span className="text-gray-300 dark:text-slate-600">-</span>
        ),
      },
      {
        key: 'date',
        header: 'วันที่ระงับ',
        render: (item) => (
          <span className="text-gray-600 dark:text-slate-300 text-sm">
            {new Date(item.suspension_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        ),
      },
      {
        key: 'attachment',
        header: 'เอกสาร',
        render: (item) => item.attachment_url ? (
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewImage(item.attachment_url); }}
            className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            title="ดูเอกสาร"
          >
            <FontAwesomeIcon icon={faImage} className="text-lg" />
          </button>
        ) : (
          <span className="text-gray-300 dark:text-slate-600">-</span>
        ),
      },
    ];

    if (activeTab === 'suspended') {
      return [
        ...base,
        {
          key: 'actions',
          header: 'ดำเนินการ',
          width: '180px',
          render: (item) => (
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
                แก้ไข
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); openUnsuspendModal(item); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                ปลดระงับ
              </button>
            </div>
          ),
        },
      ];
    }

    return [
      ...base,
      {
        key: 'release_cost',
        header: 'ค่าปลดอายัด',
        render: (item) => (
          <span className="font-semibold text-orange-600 dark:text-orange-400">{fmtBaht(item.release_cost || 0)}</span>
        ),
      },
      {
        key: 'remaining',
        header: 'คงเหลือ',
        render: (item) => (
          <span className="font-semibold text-green-600 dark:text-green-400">{fmtBaht(item.remaining_amount || 0)}</span>
        ),
      },
      {
        key: 'unsuspended_at',
        header: 'ปลดระงับ',
        render: (item) => (
          <div className="text-xs text-green-600 dark:text-green-400">
            <p className="font-medium">
              {item.unsuspended_at
                ? new Date(item.unsuspended_at).toLocaleDateString('th-TH', {
                    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })
                : '-'}
            </p>
            {item.unsuspended_by && (
              <p className="text-gray-400 dark:text-slate-500">โดย {item.unsuspended_by}</p>
            )}
          </div>
        ),
      },
    ];
  }, [activeTab]);

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faBan}
        title="จัดการบัญชีถูกระงับ"
        subtitle={`จัดการบัญชีธนาคารที่ถูกระงับจากหน่วยงานภาครัฐ • ${suspensions.length.toLocaleString()} รายการ`}
        actions={
          <UiButton icon={faPlus} onClick={() => { resetForm(); setShowCreateModal(true); }}>
            เพิ่มบัญชีถูกระงับ
          </UiButton>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={4} />

      <UiFilterPanel onSearch={() => {}}>
        <div className="w-fit shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <div className="inline-flex items-stretch h-10 gap-0.5 border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 w-fit">
            {([
              { key: 'suspended' as const, label: 'ถูกระงับ', count: suspendedCount, icon: faLock },
              { key: 'unsuspended' as const, label: 'ปลดระงับแล้ว', count: unsuspendedCount, icon: faLockOpen },
            ]).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 px-3 h-full rounded-md text-sm font-medium whitespace-nowrap transition-all
                  ${activeTab === tab.key
                    ? tab.key === 'suspended'
                      ? 'bg-white dark:bg-slate-600 text-red-600 dark:text-red-400 shadow-sm'
                      : 'bg-white dark:bg-slate-600 text-green-600 dark:text-green-400 shadow-sm'
                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                  }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="text-xs" />
                {tab.label}
                <span className={`px-1.5 min-w-[1.25rem] text-center rounded-full text-xs font-bold leading-none py-0.5
                  ${activeTab === tab.key
                    ? 'bg-gray-100 dark:bg-slate-500 text-gray-600 dark:text-slate-200'
                    : 'bg-gray-200/60 dark:bg-slate-600/60 text-gray-400 dark:text-slate-500'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </UiFilterPanel>

      <DataTable
        data={filtered}
        columns={columns}
        loading={loading}
        keyExtractor={(item) => item.id}
        emptyMessage="ไม่มีรายการ"
        emptyIcon={
          <FontAwesomeIcon
            icon={activeTab === 'suspended' ? faLock : faLockOpen}
            className="text-5xl text-gray-200 dark:text-slate-600"
          />
        }
        hoverable
        compact
        pagination={{ enabled: true, pageSize: 10, pageSizeOptions: [10, 20, 50], showPageSizeSelector: true, showInfo: true }}
      />

      {activeTab === 'unsuspended' && unsuspendedItems.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-bold text-gray-800 dark:text-slate-100 mb-3 flex items-center gap-2">
            <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-indigo-500" />
            รายงานสรุปการปลดอายัด
          </h3>
          <UiStatsGrid cards={reportCards} cols={4} />
        </div>
      )}

      <UiModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="เพิ่มบัญชีถูกระงับ"
        icon={faBan}
        iconColor="text-red-500"
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowCreateModal(false)} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              variant="danger"
              icon={faPlus}
              loading={creating}
              onClick={() => (document.getElementById('create-suspension-form') as HTMLFormElement)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              เพิ่มรายการระงับ
            </UiButton>
          </div>
        }
      >
        <form id="create-suspension-form" onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faUniversity} className="mr-1 normal-case" />
              บัญชีธนาคาร *
            </label>
            <select
              value={formData.bank_id}
              onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
              className={INPUT_CLASS}
              required
            >
              <option value="">-- เลือกบัญชีธนาคาร --</option>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  [{bank.bank_code}] {bank.name} - {bank.account_number}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faMoneyBill} className="mr-1 normal-case" />
              ยอดที่ถูกระงับ (บาท)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.suspended_amount}
              onChange={(e) => setFormData({ ...formData, suspended_amount: e.target.value })}
              className={INPUT_CLASS}
              placeholder="0.00"
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faBuilding} className="mr-1 normal-case" />
              หน่วยงานที่ระงับ *
            </label>
            <input
              type="text"
              value={formData.agency_name}
              onChange={(e) => setFormData({ ...formData, agency_name: e.target.value })}
              className={INPUT_CLASS}
              placeholder="เช่น กรมสอบสวนคดีพิเศษ (DSI), ปปง., ตำรวจ ฯลฯ"
              required
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faFileAlt} className="mr-1 normal-case" />
              รายละเอียด
            </label>
            <textarea
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              className={TEXTAREA_CLASS}
              rows={3}
              placeholder="รายละเอียดเพิ่มเติม เช่น เลขที่คำสั่ง, เหตุผล ฯลฯ"
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faUser} className="mr-1 normal-case" />
              ผู้รับผิดชอบ
            </label>
            <input
              type="text"
              value={formData.responsible_person}
              onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
              className={INPUT_CLASS}
              placeholder="ชื่อผู้รับผิดชอบ"
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 normal-case" />
              วันที่ระงับ *
            </label>
            <input
              type="date"
              value={formData.suspension_date}
              onChange={(e) => setFormData({ ...formData, suspension_date: e.target.value })}
              className={INPUT_CLASS}
              required
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>
              <FontAwesomeIcon icon={faImage} className="mr-1 normal-case" />
              แนบเอกสาร (ถ้ามี)
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileChange}
              className={FILE_INPUT_CLASS}
            />
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">รองรับ JPEG, PNG, WebP, GIF (สูงสุด 5MB)</p>
            {attachmentPreview && (
              <div className="mt-2 relative inline-block">
                <img src={attachmentPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border border-gray-200 dark:border-slate-600" />
                <button
                  type="button"
                  onClick={() => { setAttachment(null); setAttachmentPreview(null); }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </div>
        </form>
      </UiModal>

      <UiModal
        open={showEditModal && !!editingItem}
        onClose={() => { setShowEditModal(false); setEditingItem(null); }}
        title="แก้ไขรายการระงับ"
        icon={faEdit}
        iconColor="text-blue-500"
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowEditModal(false)} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              icon={faEdit}
              loading={updating}
              onClick={() => (document.getElementById('edit-suspension-form') as HTMLFormElement)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              บันทึกการแก้ไข
            </UiButton>
          </div>
        }
      >
        {editingItem && (
          <form id="edit-suspension-form" onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faUniversity} className="mr-1 normal-case" />
                บัญชีธนาคาร *
              </label>
              <select
                value={editFormData.bank_id}
                onChange={(e) => setEditFormData({ ...editFormData, bank_id: e.target.value })}
                className={INPUT_CLASS}
                required
              >
                <option value="">-- เลือกบัญชีธนาคาร --</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    [{bank.bank_code}] {bank.name} - {bank.account_number}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faMoneyBill} className="mr-1 normal-case" />
                ยอดที่ถูกระงับ (บาท)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={editFormData.suspended_amount}
                onChange={(e) => setEditFormData({ ...editFormData, suspended_amount: e.target.value })}
                className={INPUT_CLASS}
                placeholder="0.00"
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faBuilding} className="mr-1 normal-case" />
                หน่วยงานที่ระงับ *
              </label>
              <input
                type="text"
                value={editFormData.agency_name}
                onChange={(e) => setEditFormData({ ...editFormData, agency_name: e.target.value })}
                className={INPUT_CLASS}
                placeholder="เช่น กรมสอบสวนคดีพิเศษ (DSI), ปปง., ตำรวจ ฯลฯ"
                required
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faFileAlt} className="mr-1 normal-case" />
                รายละเอียด
              </label>
              <textarea
                value={editFormData.details}
                onChange={(e) => setEditFormData({ ...editFormData, details: e.target.value })}
                className={TEXTAREA_CLASS}
                rows={3}
                placeholder="รายละเอียดเพิ่มเติม เช่น เลขที่คำสั่ง, เหตุผล ฯลฯ"
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faUser} className="mr-1 normal-case" />
                ผู้รับผิดชอบ
              </label>
              <input
                type="text"
                value={editFormData.responsible_person}
                onChange={(e) => setEditFormData({ ...editFormData, responsible_person: e.target.value })}
                className={INPUT_CLASS}
                placeholder="ชื่อผู้รับผิดชอบ"
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 normal-case" />
                วันที่ระงับ *
              </label>
              <input
                type="date"
                value={editFormData.suspension_date}
                onChange={(e) => setEditFormData({ ...editFormData, suspension_date: e.target.value })}
                className={INPUT_CLASS}
                required
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faImage} className="mr-1 normal-case" />
                เอกสารแนบ
              </label>

              {editingItem.attachment_url && !removeExistingAttachment && !editAttachment && (
                <div className="mb-2 relative inline-block">
                  <img src={editingItem.attachment_url} alt="เอกสารเดิม" className="w-32 h-32 object-cover rounded-lg border border-gray-200 dark:border-slate-600" />
                  <button
                    type="button"
                    onClick={() => setRemoveExistingAttachment(true)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    title="ลบรูปเดิม"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              {editAttachmentPreview && (
                <div className="mb-2 relative inline-block">
                  <img src={editAttachmentPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border border-gray-200 dark:border-slate-600" />
                  <button
                    type="button"
                    onClick={() => { setEditAttachment(null); setEditAttachmentPreview(null); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleEditFileChange}
                className={FILE_INPUT_BLUE_CLASS}
              />
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">รองรับ JPEG, PNG, WebP, GIF (สูงสุด 5MB)</p>
            </div>
          </form>
        )}
      </UiModal>

      <UiModal
        open={showUnsuspendModal && !!unsuspendItem}
        onClose={() => setShowUnsuspendModal(false)}
        title="ปลดระงับบัญชี"
        icon={faCheckCircle}
        iconColor="text-green-500"
        size="md"
        footer={
          unsuspendItem ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={() => setShowUnsuspendModal(false)} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                icon={faCheckCircle}
                loading={unsuspendingId === unsuspendItem.id}
                onClick={handleUnsuspend}
                className="flex-1 justify-center !bg-green-600 hover:!bg-green-700"
              >
                ยืนยันปลดระงับ
              </UiButton>
            </div>
          ) : undefined
        }
      >
        {unsuspendItem && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">บัญชีธนาคาร</p>
              <p className="font-medium text-gray-800 dark:text-slate-100">
                [{unsuspendItem.banks?.bank_code}] {unsuspendItem.banks?.name} - {unsuspendItem.banks?.account_number}
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">ยอดถูกระงับ</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{fmtBaht(unsuspendItem.suspended_amount)}</p>
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faCalculator} className="mr-1 normal-case" />
                ค่าใช้จ่ายในการปลดอายัด (บาท)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={releaseCost}
                onChange={(e) => setReleaseCost(e.target.value)}
                className={INPUT_CLASS}
                placeholder="0.00"
              />
            </div>

            <div className="bg-gradient-to-r from-gray-50 to-green-50 dark:from-slate-700/50 dark:to-green-900/20 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-slate-300">ยอดถูกระงับ</span>
                <span className="font-semibold text-red-600 dark:text-red-400">{fmtBaht(unsuspendItem.suspended_amount)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-slate-300">
                  <FontAwesomeIcon icon={faMinus} className="mr-1 text-xs" />
                  ค่าปลดอายัด
                </span>
                <span className="font-semibold text-orange-600 dark:text-orange-400">{fmtBaht(parseFloat(releaseCost) || 0)}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-slate-600 pt-2 flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-slate-200">
                  <FontAwesomeIcon icon={faArrowRight} className="mr-1 text-xs" />
                  ยอดคงเหลือ
                </span>
                <span className={`text-lg font-bold ${unsuspendPreviewRemaining >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {fmtBaht(unsuspendPreviewRemaining)}
                </span>
              </div>
            </div>
          </>
        )}
      </UiModal>

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-600 z-10"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <img
              src={previewImage}
              alt="เอกสารแนบ"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BankSuspensionPage;
